import { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Tag,
  Button,
  Input,
  Space,
  Empty,
  Spin,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Typography,
  Alert,
  List,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  GlobalOutlined,
  SearchOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  LinkOutlined,
  CloudUploadOutlined,
  BookOutlined,
} from "@ant-design/icons";
import {
  fetchReferenceSources,
  uploadReferenceDocument,
  startReferenceCrawl,
  searchReferenceLibrary,
  deleteReferenceSource,
} from "../../../service/matter";

const { Title, Text, Paragraph } = Typography;

const POLL_INTERVAL = 30_000;
const STATUS_COLORS = {
  pending: "default",
  indexing: "processing",
  completed: "success",
  error: "error",
};

const ALLOWED_DOMAINS = [
  "law.cornell.edu",
  "ecfr.gov",
  "va.gov",
  "benefits.va.gov",
  "knowva.ebenefits.va.gov",
];

const ReferenceLibrary = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [searching, setSearching] = useState(false);
  const [crawlUrl, setCrawlUrl] = useState("");
  const [crawlTitle, setCrawlTitle] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [uploadNotice, setUploadNotice] = useState(null);
  const [sourceLoadError, setSourceLoadError] = useState(null);
  const intervalRef = useRef(null);
  const uploadInputRef = useRef(null);

  const loadSources = useCallback(async () => {
    try {
      const data = await fetchReferenceSources();
      setSources(data.sources || []);
      setSourceLoadError(null);
    } catch (err) {
      console.error("Failed to load reference sources:", err);
      setSourceLoadError(
        err.response?.data?.error ||
          "Reference Library sources could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSources();
    intervalRef.current = setInterval(loadSources, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [loadSources]);

  const handleUpload = async (file) => {
    if (!file) return;

    const name = file.name || "";
    const ext = name.split(".").pop().toLowerCase();
    if (ext !== "pdf") {
      const msg = "Only PDF files can be uploaded as reference documents.";
      setUploadNotice({ type: "error", message: msg });
      message.error(msg);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
      return;
    }

    setUploading(true);
    setUploadNotice({ type: "info", message: `Uploading ${name}...` });
    try {
      const result = await uploadReferenceDocument(file, name);
      const msg = result.message || "Reference document uploaded.";
      setUploadNotice({ type: "success", message: msg });
      message.success(msg);
      await loadSources();
    } catch (err) {
      const msg = err.response?.data?.error || "Upload failed.";
      setUploadNotice({ type: "error", message: msg });
      message.error(msg);
    } finally {
      setUploading(false);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    handleUpload(file);
  };

  const handleCrawl = async () => {
    if (!crawlUrl.trim()) {
      message.warning("Enter a URL to crawl.");
      return;
    }

    setCrawling(true);
    try {
      const result = await startReferenceCrawl(crawlUrl.trim(), crawlTitle.trim());
      message.success(result.message || "Crawl started.");
      setCrawlUrl("");
      setCrawlTitle("");
      loadSources();
    } catch (err) {
      const msg = err.response?.data?.error || "Crawl failed.";
      message.error(msg);
    } finally {
      setCrawling(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const data = await searchReferenceLibrary(searchQuery.trim());
      setSearchResults(data.results || []);
    } catch (err) {
      message.error("Search failed.");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteReferenceSource(id);
      message.success("Reference source deleted.");
      loadSources();
    } catch (err) {
      message.error("Delete failed.");
    }
  };

  const pdfCount = sources.filter((s) => s.source_type === "pdf").length;
  const webCount = sources.filter((s) => s.source_type === "web").length;
  const totalChunks = sources.reduce((sum, s) => sum + (s.item_count || 0), 0);
  const indexingCount = sources.filter((s) => s.status === "indexing").length;

  const columns = [
    {
      title: "Type",
      dataIndex: "source_type",
      key: "type",
      width: 70,
      render: (type) =>
        type === "pdf" ? (
          <Tooltip title="PDF Upload">
            <FilePdfOutlined style={{ fontSize: 18, color: "#cf1322" }} />
          </Tooltip>
        ) : (
          <Tooltip title="Web Crawl">
            <GlobalOutlined style={{ fontSize: 18, color: "#0C53BB" }} />
          </Tooltip>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {record.url_or_path && record.source_type === "web" && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.url_or_path.length > 80
                ? record.url_or_path.substring(0, 80) + "..."
                : record.url_or_path}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => (
        <Space direction="vertical" size={2} align="center">
          <Tag color={STATUS_COLORS[status || "pending"] || "default"}>
            {(status || "pending").charAt(0).toUpperCase() + (status || "pending").slice(1)}
          </Tag>
          {status === "indexing" && record.total_items > 0 && (
            <Progress
              percent={Math.round(
                ((record.item_count || 0) / record.total_items) * 100
              )}
              size="small"
              style={{ width: 80 }}
            />
          )}
        </Space>
      ),
    },
    {
      title: "Chunks",
      dataIndex: "item_count",
      key: "chunks",
      width: 80,
      align: "right",
      render: (count) => (count || 0).toLocaleString(),
    },
    {
      title: "Updated",
      dataIndex: "last_updated",
      key: "updated",
      width: 160,
      render: (ts) => {
        if (!ts) return "—";
        const d = new Date(ts);
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      },
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, record) => (
        <Popconfirm
          title="Delete this reference source?"
          description="This will remove the source and its vectors from the search index."
          onConfirm={() => handleDelete(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: "#8c8c8c" }}>
          Loading Reference Library...
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 4 }}>
          <BookOutlined style={{ marginRight: 8 }} />
          Reference Library
        </Title>
        <Text type="secondary">
          Shared legal knowledge base used by the AI agent. Upload reference
          PDFs or crawl approved legal sources to build the library.
        </Text>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="PDF Sources"
              value={pdfCount}
              prefix={<FilePdfOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Web Sources"
              value={webCount}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Chunks"
              value={totalChunks}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Indexing Now"
              value={indexingCount}
              valueStyle={
                indexingCount > 0 ? { color: "#0C53BB" } : undefined
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Upload Section */}
      <Card
        title={
          <Space>
            <CloudUploadOutlined />
            Upload Reference PDF
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <input
          ref={uploadInputRef}
          type="file"
          accept="application/pdf,.pdf"
          data-testid="reference-pdf-upload-input"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />
        <Button
          icon={<UploadOutlined />}
          loading={uploading}
          type="primary"
          onClick={() => uploadInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Choose PDF"}
        </Button>
        <Text
          type="secondary"
          style={{ marginLeft: 12, fontSize: 12 }}
        >
          Uploaded PDFs are processed through the extraction and embedding
          pipeline (categorization is skipped).
        </Text>
        {uploadNotice && (
          <Alert
            type={uploadNotice.type}
            showIcon
            message={uploadNotice.message}
            style={{ marginTop: 12 }}
          />
        )}
        {sourceLoadError && (
          <Alert
            type="error"
            showIcon
            message={sourceLoadError}
            style={{ marginTop: 12 }}
          />
        )}
      </Card>

      {/* Crawl Section */}
      <Card
        title={
          <Space>
            <LinkOutlined />
            Crawl Web Source
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Input
            placeholder="URL to crawl (e.g. https://www.law.cornell.edu/uscode/text/38)"
            value={crawlUrl}
            onChange={(e) => setCrawlUrl(e.target.value)}
            onPressEnter={handleCrawl}
            prefix={<GlobalOutlined />}
          />
          <Space>
            <Input
              placeholder="Display title (optional)"
              value={crawlTitle}
              onChange={(e) => setCrawlTitle(e.target.value)}
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              onClick={handleCrawl}
              loading={crawling}
              disabled={!crawlUrl.trim()}
            >
              {crawling ? "Starting..." : "Start Crawler"}
            </Button>
          </Space>
          <Alert
            type="info"
            showIcon
            message="Allowed domains"
            description={
              <span>
                {ALLOWED_DOMAINS.map((d) => (
                  <Tag key={d} style={{ marginBottom: 4 }}>
                    {d}
                  </Tag>
                ))}
              </span>
            }
            style={{ marginTop: 4 }}
          />
        </Space>
      </Card>

      {/* Sources Table */}
      <Card
        title={
          <Space>
            <BookOutlined />
            Indexed Sources
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadSources}
              type="text"
            />
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Auto-refreshes every 30s
          </Text>
        }
      >
        <Table
          dataSource={sources}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={sources.length > 20 ? { pageSize: 20 } : false}
          locale={{
            emptyText: (
              <Empty
                description="No reference sources yet. Upload a PDF or start a web crawl."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>

      {/* Search Section */}
      <Card
        title={
          <Space>
            <SearchOutlined />
            Search Reference Library
          </Space>
        }
        size="small"
      >
        <Space style={{ width: "100%", marginBottom: 16 }}>
          <Input
            placeholder="Search the reference library..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 500 }}
            prefix={<SearchOutlined />}
          />
          <Button
            type="primary"
            onClick={handleSearch}
            loading={searching}
            disabled={!searchQuery.trim()}
          >
            Search
          </Button>
        </Space>

        {searching && (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin />
          </div>
        )}

        {searchResults !== null && !searching && (
          <>
            {searchResults.length === 0 ? (
              <Empty
                description="No results found."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                dataSource={searchResults}
                renderItem={(item, idx) => (
                  <List.Item key={item.id}>
                    <List.Item.Meta
                      avatar={
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            background: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            color: "#595959",
                          }}
                        >
                          {idx + 1}
                        </div>
                      }
                      title={
                        <Space>
                          <Text strong>{item.title || "Untitled"}</Text>
                          <Tag
                            color={
                              item.source_type === "pdf" ? "red" : "blue"
                            }
                          >
                            {item.source_type === "pdf" ? "PDF" : "Web"}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Score: {((item.score || 0) * 100).toFixed(1)}%
                          </Text>
                        </Space>
                      }
                      description={
                        <Paragraph
                          ellipsis={{ rows: 3, expandable: true }}
                          style={{ margin: 0, color: "#595959" }}
                        >
                          {item.text}
                        </Paragraph>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ReferenceLibrary;
